// MerchantStatusBanner — non-dismissible notice shown at the top of the
// authed shell when the currently-selected merchant is suspended or closed.
//
// PDF review 26 May 26 round 2: "Merchant page – list of merchants – what
// happens when the merchant status moves to suspended or closed? → show
// banner and block payouts." The banner is the user-facing half; the
// server-side block lives in payout.Service.assertMerchantActive.
//
// Suspended ≠ closed:
//   • suspended → recoverable; ops can re-activate; copy implies "fix it"
//   • closed    → terminal; copy implies "contact support / archive"
//
// Banner is hidden when:
//   • No merchant is selected (admin viewing the platform-wide overview)
//   • Status is "active" (the normal case)
//   • Status couldn't be resolved (defensive — don't false-positive)
import {
  useCurrentMerchantStatus,
  isMerchantBlocked,
} from "@/queries/useCurrentMerchantStatus";

export function MerchantStatusBanner() {
  const { label, status } = useCurrentMerchantStatus();

  if (!isMerchantBlocked(status)) {
    return null;
  }

  const isClosed = status === "closed";
  const title = isClosed
    ? `${label ?? "This merchant"} is closed`
    : `${label ?? "This merchant"} is suspended`;

  const body = isClosed
    ? "Payouts and approvals are blocked. Existing data is read-only. Contact platform admin to re-open the account if needed."
    : "Payouts and approvals are temporarily blocked. Existing data remains read-only until the merchant is re-activated.";

  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        background: isClosed ? "var(--bad-soft, #fee)" : "var(--warn-soft, #fff7e6)",
        borderBottom: `1px solid ${isClosed ? "var(--bad, #c33)" : "var(--warn, #b07)"}`,
        padding: "10px 20px",
        fontSize: 13,
        display: "flex",
        gap: 12,
        alignItems: "center",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          fontSize: 16,
          lineHeight: 1,
          color: isClosed ? "var(--bad, #c33)" : "var(--warn, #b07)",
        }}
      >
        ●
      </span>
      <div>
        <strong style={{ fontWeight: 600 }}>{title}.</strong>{" "}
        <span style={{ color: "var(--ink-2)" }}>{body}</span>
      </div>
    </div>
  );
}
